import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClientService } from '../../../shared/services/http-client/http-client.service';
import {
  BeneficioPlanoResponseDto,
  BeneficioResponseDto,
  BeneficiosPlanoQueryParams,
  CatalogoBeneficiosQueryParams,
  ComprovacaoElegibilidadeResponseDto,
  ElegibilidadeBeneficioResponseDto,
  PlanoAntecipacaoResponseDto,
  PlanoOpcoesRendaResponseDto,
  PlanoRentabilidadeResponseDto,
  PlanoValorMinimoSaldoResponseDto,
  SalvarElegibilidadeBeneficioCommandDto,
  SalvarPlanoAntecipacaoCommandDto,
  SalvarPlanoOpcoesRendaCommandDto,
  SalvarPlanoRentabilidadeCommandDto,
  SalvarPlanoValorMinimoSaldoCommandDto,
} from '../../models/parametros/beneficios-api.model';

const PLANOS_PARAM_BASE_PATH = '/v1/planos-param';
const BENEFICIOS_BASE_PATH = '/v1/beneficios';
const COMPROVACOES_ELEGIBILIDADE_BASE_PATH = '/v1/comprovacoes-elegibilidade';

@Injectable({
  providedIn: 'root',
})
export class BeneficiosApiService {
  constructor(private readonly httpClient: HttpClientService) {}

  obterAntecipacao(idPlano: number): Observable<PlanoAntecipacaoResponseDto> {
    return this.httpClient.get<PlanoAntecipacaoResponseDto>(this.antecipacaoPath(idPlano));
  }

  salvarAntecipacao(idPlano: number, command: SalvarPlanoAntecipacaoCommandDto): Observable<PlanoAntecipacaoResponseDto> {
    return this.httpClient.put<PlanoAntecipacaoResponseDto>(this.antecipacaoPath(idPlano), command);
  }

  obterOpcoesRenda(idPlano: number): Observable<PlanoOpcoesRendaResponseDto> {
    return this.httpClient.get<PlanoOpcoesRendaResponseDto>(this.opcoesRendaPath(idPlano));
  }

  salvarOpcoesRenda(idPlano: number, command: SalvarPlanoOpcoesRendaCommandDto): Observable<PlanoOpcoesRendaResponseDto> {
    return this.httpClient.put<PlanoOpcoesRendaResponseDto>(this.opcoesRendaPath(idPlano), command);
  }

  obterRentabilidade(idPlano: number): Observable<PlanoRentabilidadeResponseDto> {
    return this.httpClient.get<PlanoRentabilidadeResponseDto>(this.rentabilidadePath(idPlano));
  }

  salvarRentabilidade(idPlano: number, command: SalvarPlanoRentabilidadeCommandDto): Observable<PlanoRentabilidadeResponseDto> {
    return this.httpClient.put<PlanoRentabilidadeResponseDto>(this.rentabilidadePath(idPlano), command);
  }

  obterValorMinimoSaldo(idPlano: number): Observable<PlanoValorMinimoSaldoResponseDto> {
    return this.httpClient.get<PlanoValorMinimoSaldoResponseDto>(this.valorMinimoSaldoPath(idPlano));
  }

  salvarValorMinimoSaldo(
    idPlano: number,
    command: SalvarPlanoValorMinimoSaldoCommandDto,
  ): Observable<PlanoValorMinimoSaldoResponseDto> {
    return this.httpClient.put<PlanoValorMinimoSaldoResponseDto>(this.valorMinimoSaldoPath(idPlano), command);
  }

  listarCatalogo(params?: CatalogoBeneficiosQueryParams): Observable<BeneficioResponseDto[]> {
    return this.httpClient.get<BeneficioResponseDto[]>(BENEFICIOS_BASE_PATH, { params: this.toHttpParams(params) });
  }

  listarComprovacoes(params?: CatalogoBeneficiosQueryParams): Observable<ComprovacaoElegibilidadeResponseDto[]> {
    return this.httpClient.get<ComprovacaoElegibilidadeResponseDto[]>(COMPROVACOES_ELEGIBILIDADE_BASE_PATH, {
      params: this.toHttpParams(params),
    });
  }

  /** Sem filtro, inclui os removidos pela lixeira. */
  listarBeneficiosPlano(idPlano: number, params?: BeneficiosPlanoQueryParams): Observable<BeneficioPlanoResponseDto[]> {
    return this.httpClient.get<BeneficioPlanoResponseDto[]>(this.beneficiosPlanoPath(idPlano), {
      params: this.toHttpParams(params),
    });
  }

  /** `id` nulo na resposta = benefício ainda não está no plano. */
  obterElegibilidade(idPlano: number, idBeneficio: number): Observable<ElegibilidadeBeneficioResponseDto> {
    return this.httpClient.get<ElegibilidadeBeneficioResponseDto>(this.elegibilidadePath(idPlano, idBeneficio));
  }

  /** Cria ou reativa o benefício no plano e substitui as regras; devolve o agregado gravado. */
  salvarElegibilidade(
    idPlano: number,
    idBeneficio: number,
    command: SalvarElegibilidadeBeneficioCommandDto,
  ): Observable<ElegibilidadeBeneficioResponseDto> {
    return this.httpClient.put<ElegibilidadeBeneficioResponseDto>(this.elegibilidadePath(idPlano, idBeneficio), command);
  }

  /** Toggle "Ativo no autoatendimento" fora do Salvar. */
  alterarAutoatendimento(idPlano: number, idBeneficio: number, ativo: boolean): Observable<BeneficioPlanoResponseDto> {
    return this.httpClient.patch<BeneficioPlanoResponseDto>(this.beneficioPlanoPath(idPlano, idBeneficio), {
      fl_ativo: ativo,
    });
  }

  /** Lixeira do card (`false`) ou devolução ao plano (`true`); as regras ficam guardadas. */
  alterarStatusNoPlano(idPlano: number, idBeneficio: number, status: boolean): Observable<BeneficioPlanoResponseDto> {
    return this.httpClient.patch<BeneficioPlanoResponseDto>(`${this.beneficioPlanoPath(idPlano, idBeneficio)}/status`, {
      fl_status: status,
    });
  }

  private antecipacaoPath(idPlano: number): string {
    return `${PLANOS_PARAM_BASE_PATH}/${idPlano}/antecipacao`;
  }

  private opcoesRendaPath(idPlano: number): string {
    return `${PLANOS_PARAM_BASE_PATH}/${idPlano}/opcoes-renda`;
  }

  private rentabilidadePath(idPlano: number): string {
    return `${PLANOS_PARAM_BASE_PATH}/${idPlano}/rentabilidade`;
  }

  private valorMinimoSaldoPath(idPlano: number): string {
    return `${PLANOS_PARAM_BASE_PATH}/${idPlano}/valor-minimo-saldo`;
  }

  private beneficiosPlanoPath(idPlano: number): string {
    return `${PLANOS_PARAM_BASE_PATH}/${idPlano}/beneficios`;
  }

  private beneficioPlanoPath(idPlano: number, idBeneficio: number): string {
    return `${this.beneficiosPlanoPath(idPlano)}/${idBeneficio}`;
  }

  private elegibilidadePath(idPlano: number, idBeneficio: number): string {
    return `${this.beneficioPlanoPath(idPlano, idBeneficio)}/elegibilidade`;
  }

  private toHttpParams(params?: CatalogoBeneficiosQueryParams | BeneficiosPlanoQueryParams): HttpParams | undefined {
    if (!params) {
      return undefined;
    }

    let httpParams = new HttpParams();

    for (const [chave, valor] of Object.entries(params)) {
      if (valor != null && valor !== '') {
        httpParams = httpParams.set(chave, String(valor));
      }
    }

    return httpParams.keys().length > 0 ? httpParams : undefined;
  }
}
